import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  Modal,
  TextField,
  Typography,
  Chip,
  Stack,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CountrySelect from "components/CountrySelect";
import ProductionParkMeteringPoints from "../Components/ProductionParkMeteringPoints";
import ComponentForm from "./ComponentForm";
import BatteryForm from "./BatteryForm";
import { CountryType } from "api/models/CountryType";
import { ProductionPark } from "api/models/ProductionPark";
import { Battery } from "api/models/Battery";
import ConfirmationModal from "components/Modals/Confirmation";
import ClientSelect from "components/ClientSelect/ClientSelect";
import { SelectChangeEvent } from "@mui/material/Select";
import { useAppSelector, useAppDispatch } from "hooks";
import {
  selectMe,
  selectProductionParkLimitations,
  selectForecastTemplates,
} from "store/selectors";
import { fetchAllForecastTemplates } from "store/appSlice";
import LimitModal from "./LimitModal";
import EditLimitModal from "./EditLimitModal";
import HistoryModal from "./HistoryModal";
import EditCodeModal from "./EditCodeModal";
import { fetchProductionParkLimitations } from "store/appSlice";
import Timeline from "@mui/lab/Timeline";
import TimelineItem, { timelineItemClasses } from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import HistoryIcon from "@mui/icons-material/History";
import dayjs from "dayjs";
import { ProductionParkLimitationTypes } from "helpers/enums";
import Select from "components/Select";
import ChangeHistoryModal from "./ChangeHistoryModal";

interface ProductionParkFormProps {
  formData: ProductionPark;
  setFormData: React.Dispatch<React.SetStateAction<ProductionPark>>;
  selectedCountry: CountryType;
  handleCountrySelect: (country: CountryType) => void;
  selectedClients: { title: string; value: number }[];
  setSelectedClients: React.Dispatch<
    React.SetStateAction<{ title: string; value: number }[]>
  >;
  handleChange: (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>,
    index?: number,
  ) => void;
  handleRemoveComponent: (index: number) => void;
  handleAddComponent: () => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  deleteModalOpen?: boolean;
  openDeleteModal?: () => void;
  closeDeleteModal?: () => void;
  handleDelete?: () => void;
  showDeleteButton: boolean;
  productionParkId?: number;
  setSnackbar: any;
  isEditMode?: boolean;
  productionParkType: string;
  batteries: Battery[];
  handleBatteryChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => void;
  handleRemoveBattery: (index: number) => void;
  handleAddBattery: () => void;
  handleCloneBattery?: (index: number) => void;
}

const ProductionParkForm: React.FC<ProductionParkFormProps> = ({
  formData,
  setFormData,
  selectedCountry,
  handleCountrySelect,
  selectedClients,
  setSelectedClients,
  handleChange,
  handleRemoveComponent,
  handleAddComponent,
  handleSubmit,
  deleteModalOpen,
  openDeleteModal,
  closeDeleteModal,
  handleDelete,
  showDeleteButton,
  productionParkId,
  setSnackbar,
  productionParkType,
  isEditMode = false,
  batteries,
  handleBatteryChange,
  handleRemoveBattery,
  handleAddBattery,
  handleCloneBattery,
}) => {
  const [isFormValid, setIsFormValid] = useState(false);
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [limitModal, setLimitModal] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);
  const [editLimitModal, setEditLimitModal] = useState<any>(false);
  const [changeHistoryModal, setChangeHistoryModal] = useState<any>(false);
  const [editCodeModal, setEditCodeModal] = useState(false);
  const [batteriesExpanded, setBatteriesExpanded] = useState<boolean | null>(
    null,
  );
  const me = useAppSelector(selectMe);
  const dispatch = useAppDispatch();
  const productionParkLimitations = useAppSelector(
    selectProductionParkLimitations,
  );
  const consumptionForecastSamples = useAppSelector(selectForecastTemplates);

  const isClientAdmin = me?.roles.some(
    (role) => role.name === "ROLE_CLIENT_ADMIN" && role.isCurrentRole,
  );
  const isClientUser = me?.roles.some(
    (role) => role.name === "ROLE_CLIENT_USER" && role.isCurrentRole,
  );

  const isAdmin = me?.roles.some(
    (role) => role.name === "ROLE_ADMIN" && role.isCurrentRole,
  );

  useEffect(() => {
    if (productionParkId) {
      dispatch(
        fetchProductionParkLimitations(
          productionParkId,
          dayjs().subtract(7, "day").format("YYYY-MM-DDTHH:mm"),
        ),
      );
    } else {
      dispatch(
        fetchProductionParkLimitations(
          null,
          dayjs().subtract(7, "day").format("YYYY-MM-DDTHH:mm"),
        ),
      );
    }
    if (isAdmin) {
      dispatch(fetchAllForecastTemplates());
    }
  }, [productionParkId, dispatch, isAdmin]);

  useEffect(() => {
    if (formData.location) {
      const [lon, lat] = formData.location.split(",");
      setLatitude(lat.trim());
      setLongitude(lon.trim());
    }
  }, [formData.location]);

  const handleLatitudeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLatitude(e.target.value);
    setFormData({ ...formData, location: `${longitude}, ${e.target.value}` });
  };

  const handleLongitudeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLongitude(e.target.value);
    setFormData({ ...formData, location: `${e.target.value}, ${latitude}` });
  };

  const checkFormValidity = () => {
    const { name, location } = formData;
    return name.trim() !== "" && location.trim() !== "";
  };

  const handleCodeSave = (newCode: string) => {
    setFormData({ ...formData, code: newCode });
  };

  useEffect(() => {
    setIsFormValid(checkFormValidity());
  }, [formData]);

  useEffect(() => {
    if (batteriesExpanded === null && batteries.length > 0) {
      setBatteriesExpanded(batteries.length <= 2);
    }
  }, [batteries, batteriesExpanded]);

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Grid item xs={12} sx={{ paddingTop: 1, paddingBottom: 1 }}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              size="small"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required={true}
            />
          </Grid>
          {isEditMode && (
            <Grid item sx={{ paddingTop: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sx={{ paddingTop: 1, paddingBottom: 1 }}>
                  <TextField
                    fullWidth
                    label="Code"
                    name="code"
                    size="small"
                    value={formData.code}
                    disabled
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setEditCodeModal(true)}
                            edge="end"
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid item sx={{ paddingTop: 1, paddingBottom: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={6} sx={{ paddingTop: 1, paddingBottom: 1 }}>
                <TextField
                  fullWidth
                  label="Latitude"
                  name="latitude"
                  size="small"
                  value={latitude}
                  onChange={handleLatitudeChange}
                  required={true}
                  inputProps={{ type: "number" }}
                  onWheel={(e) =>
                    e.target instanceof HTMLElement && e.target.blur()
                  }
                />
              </Grid>
              <Grid item xs={6} sx={{ paddingTop: 1, paddingBottom: 1 }}>
                <TextField
                  fullWidth
                  label="Longitude"
                  name="longitude"
                  size="small"
                  value={longitude}
                  onChange={handleLongitudeChange}
                  required={true}
                  inputProps={{ type: "number" }}
                  onWheel={(e) =>
                    e.target instanceof HTMLElement && e.target.blur()
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sx={{ paddingTop: 1, paddingBottom: 1 }}>
            <CountrySelect
              value={selectedCountry}
              setValue={handleCountrySelect}
              label="Price area"
            />
          </Grid>
          {isAdmin && (
            <Grid item sx={{ paddingTop: 1, paddingBottom: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <Select
                    label="Delivery type"
                    id="deliveryType"
                    name="deliveryType"
                    value={formData.deliveryType}
                    onChange={(e: SelectChangeEvent<string>) => {
                      setFormData({ ...formData, deliveryType: e.target.value });
                    }}
                    options={[
                      {
                        label: "Total",
                        value: "TOTAL",
                      },
                      {
                        label: "Fixed",
                        value: "FIXED",
                      },
                      {
                        label: "External",
                        value: "EXTERNAL",
                      },
                    ]}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isBigProducer || false}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            isBigProducer: e.target.checked,
                          });
                        }}
                        sx={{ ml: 1 }}
                      />
                    }
                    label="Large producer"
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid item xs={6}>
          {isAdmin && (
            <Grid item xs={12} sx={{ paddingTop: 1, paddingBottom: 1 }}>
              <ClientSelect
                formValues={formData}
                setFormValues={setFormData}
                selectedClients={selectedClients}
                setSelectedClients={setSelectedClients}
                disabled={isClientAdmin || isClientUser}
              />
            </Grid>
          )}
          {selectedClients[0]?.value && (
            <Grid item xs={12} sx={{ paddingTop: 1, paddingBottom: 1 }}>
              <ProductionParkMeteringPoints
                formValues={formData}
                setFormValues={setFormData}
                clientId={selectedClients[0]?.value || 0}
              />
            </Grid>
          )}
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="h6">Components</Typography>
          {formData.productionParkComponents.map((component, index) => {
            const componentWithDefaults = {
              ...component,
              maintenances: component.maintenances || [],
              limits: component.limits || [],
            };

            return (
              <ComponentForm
                key={index}
                handleChange={handleChange}
                data={componentWithDefaults}
                handleRemove={handleRemoveComponent}
                productionParkId={productionParkId}
                index={index}
              />
            );
          })}
          <Box paddingTop={3}>
            <Button variant="contained" onClick={handleAddComponent}>
              Add component
            </Button>
          </Box>
        </Grid>
        {isEditMode && (
          <Grid item xs={12} sx={{ mt: 3 }}>
            <Accordion
              expanded={batteriesExpanded ?? false}
              onChange={(_, isExpanded) => setBatteriesExpanded(isExpanded)}
              disableGutters
              elevation={0}
              sx={{
                backgroundColor: "transparent",
                borderRadius: "0 !important",
                paddingBottom: "1rem",
                borderBottom: "1px solid lightgrey",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ padding: 0, minHeight: "auto" }}
              >
                <Typography variant="h6">
                  Batteries ({batteries.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0, paddingTop: 2 }}>
                {isAdmin && batteries.length > 0 && (
                  <Stack
                    direction="row"
                    spacing={3}
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <Select
                      label="Consumption forecast saved sample"
                      id="consumptionForecastSampleId"
                      name="consumptionForecastSampleId"
                      maxWidth={350}
                      value={
                        formData.consumptionForecastTemplate?.id?.toString() ||
                        ""
                      }
                      onChange={(e: SelectChangeEvent<string>) => {
                        const selectedId = e.target.value
                          ? Number(e.target.value)
                          : null;
                        const selectedSample = selectedId
                          ? (
                              consumptionForecastSamples as unknown as {
                                id: number;
                                name: string;
                              }[]
                            )?.find((s) => s.id === selectedId)
                          : null;
                        setFormData({
                          ...formData,
                          consumptionForecastTemplate: selectedSample
                            ? {
                                id: selectedSample.id,
                                name: selectedSample.name,
                              }
                            : undefined,
                        });
                      }}
                      options={[
                        { label: "None", value: "" },
                        ...((
                          consumptionForecastSamples as unknown as {
                            id: number;
                            name: string;
                          }[]
                        )?.map((sample) => ({
                          label: sample.name,
                          value: sample.id.toString(),
                        })) || []),
                      ]}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.useBatteriesForSupplyPlan || false}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              useBatteriesForSupplyPlan: e.target.checked,
                            });
                          }}
                        />
                      }
                      label="Create supply plan based on battery forecast"
                    />
                  </Stack>
                )}
                <Grid container spacing={2}>
                  {batteries
                    .sort((a, b) => a.id - b.id)
                    .map((battery, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <BatteryForm
                          handleChange={handleBatteryChange}
                          data={battery}
                          handleRemove={handleRemoveBattery}
                          handleClone={handleCloneBattery}
                          index={index}
                          isAdmin={isAdmin}
                        />
                      </Grid>
                    ))}
                </Grid>
                <Box paddingTop={3}>
                  <Button variant="contained" onClick={handleAddBattery}>
                    Add battery
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
        <Grid item xs={12} sx={{ mt: 4 }}>
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Typography variant="h6">Output limitations</Typography>
            <Button
              variant="outlined"
              size="small"
              sx={{ ml: 2 }}
              onClick={() => setHistoryModal(true)}
            >
              History
            </Button>
          </Box>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          {productionParkLimitations.length > 0 && (
            <Grid item xs={6}>
              <Timeline
                sx={{
                  paddingTop: 0,
                  marginTop: "0.4rem",
                  marginLeft: 0,
                  paddingLeft: 0,
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                  },
                }}
              >
                {productionParkLimitations.map(
                  (limitation, limitationIndex) => {
                    const forecastTypes: Record<
                      string,
                      { label: string; color: string }
                    > = {
                      SUN: {
                        label: "Solar",
                        color: "warning",
                      },
                      WIND: {
                        label: "Wind",
                        color: "primary",
                      },
                    };

                    const forecastType =
                      productionParkType === "HYBRID"
                        ? forecastTypes[limitation.forecastType]
                        : null;

                    const isActive =
                      dayjs(limitation.fromDateTime) <= dayjs() &&
                      dayjs(limitation.toDateTime) >= dayjs();

                    const inThePast = dayjs(limitation.toDateTime) < dayjs();

                    const inTheFuture =
                      dayjs(limitation.fromDateTime) > dayjs();

                    return (
                      <TimelineItem key={limitation.id}>
                        <TimelineSeparator>
                          <TimelineDot variant="outlined" color="grey" />
                          {limitationIndex + 1 <
                            productionParkLimitations.length && (
                            <TimelineConnector />
                          )}
                        </TimelineSeparator>
                        <TimelineContent>
                          {isActive && (
                            <Chip
                              label="Active"
                              color="success"
                              variant="filled"
                              size="small"
                              sx={{ mb: "0.4rem" }}
                            />
                          )}
                          {inThePast && (
                            <Chip
                              label="Passed"
                              color="default"
                              variant="filled"
                              size="small"
                              sx={{ mb: "0.4rem" }}
                            />
                          )}
                          {inTheFuture && (
                            <Chip
                              label="Upcoming"
                              color="default"
                              variant="filled"
                              size="small"
                              sx={{ mb: "0.4rem" }}
                            />
                          )}
                          {forecastType && (
                            <Chip
                              label={forecastType.label}
                              color={
                                forecastType.color as
                                  | "default"
                                  | "primary"
                                  | "secondary"
                                  | "error"
                                  | "info"
                                  | "success"
                                  | "warning"
                              }
                              variant="filled"
                              size="small"
                              sx={{ ml: "0.4rem", mb: "0.4rem" }}
                            />
                          )}
                          <Typography component="p" fontWeight={600}>
                            {`${dayjs(limitation.fromDateTime).format("DD.MM.YYYY HH:mm")} - ${
                              limitation.toDateTime
                                ? dayjs(limitation.toDateTime).format(
                                    "DD.MM.YYYY HH:mm",
                                  )
                                : "..."
                            }`}
                          </Typography>
                          <Typography component="p">
                            {`Type: ${ProductionParkLimitationTypes[limitation.type as keyof typeof ProductionParkLimitationTypes]}`}
                          </Typography>
                          <Typography component="p">
                            {`Value: ${limitation.type === "MAX_OUTPUT" ? limitation.value : limitation.value * 100}`}
                            {limitation.type === "MAX_OUTPUT" ? " kW" : "%"}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ mt: "0.4rem", mb: "1rem" }}
                          >
                            {!inThePast && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setEditLimitModal(limitation)}
                              >
                                Edit
                              </Button>
                            )}
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setChangeHistoryModal(limitation)}
                            >
                              <HistoryIcon />
                            </Button>
                          </Stack>
                        </TimelineContent>
                      </TimelineItem>
                    );
                  },
                )}
              </Timeline>
            </Grid>
          )}
          <Box>
            <Button
              variant="contained"
              sx={{ mr: 1 }}
              onClick={() => setLimitModal(true)}
            >
              Add limitation
            </Button>
          </Box>
          <Modal open={limitModal} onClose={() => setLimitModal(false)}>
            <LimitModal
              setOpenModal={setLimitModal}
              productionParkId={productionParkId}
              productionParkType={productionParkType}
              dispatch={() =>
                dispatch(
                  fetchProductionParkLimitations(
                    productionParkId,
                    dayjs().subtract(7, "day").format("YYYY-MM-DDTHH:mm"),
                  ),
                )
              }
              setSnackbar={setSnackbar}
            />
          </Modal>
          <Modal open={historyModal} onClose={() => setHistoryModal(false)}>
            <HistoryModal
              setOpenModal={setHistoryModal}
              productionParkId={productionParkId}
              productionParkType={productionParkType}
              dispatch={() =>
                dispatch(
                  fetchProductionParkLimitations(
                    productionParkId,
                    dayjs().subtract(7, "day").format("YYYY-MM-DDTHH:mm"),
                  ),
                )
              }
              setSnackbar={setSnackbar}
            />
          </Modal>
          <Modal
            open={!!editLimitModal}
            onClose={() => setEditLimitModal(false)}
          >
            <EditLimitModal
              setOpenModal={setEditLimitModal}
              productionParkId={productionParkId}
              productionParkType={productionParkType}
              limit={editLimitModal}
              dispatch={() =>
                dispatch(
                  fetchProductionParkLimitations(
                    productionParkId,
                    dayjs().subtract(7, "day").format("YYYY-MM-DDTHH:mm"),
                  ),
                )
              }
              setSnackbar={setSnackbar}
            />
          </Modal>
          <Modal
            open={!!changeHistoryModal}
            onClose={() => setChangeHistoryModal(false)}
          >
            <ChangeHistoryModal limitation={changeHistoryModal} />
          </Modal>
          {isEditMode && (
            <Modal open={editCodeModal} onClose={() => setEditCodeModal(false)}>
              <EditCodeModal
                currentCode={formData.code}
                setOpenModal={setEditCodeModal}
                onSave={handleCodeSave}
              />
            </Modal>
          )}
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Divider />
        </Grid>
        <Grid item>
          <Button
            disabled={!isFormValid}
            type="submit"
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </Grid>
        {showDeleteButton && (
          <>
            <Grid item xs={2}>
              <Button
                variant="outlined"
                color="primary"
                onClick={openDeleteModal}
              >
                Delete park
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Modal open={deleteModalOpen} onClose={closeDeleteModal}>
                <Box>
                  <ConfirmationModal
                    action={handleDelete}
                    title={`Do you want to delete the park ${formData.name}?`}
                    subtitle={
                      "This will remove the park from the system, but not past forecasts and measurement data"
                    }
                    buttonTitle={"Delete park"}
                    setConfirmationModal={closeDeleteModal}
                  />
                </Box>
              </Modal>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default ProductionParkForm;
